import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { type Project } from '@/lib/ProjectsManager';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/useToast';
import { useFS } from '@/hooks/useFS';
import { useProjectsManager } from '@/hooks/useProjectsManager';
import { useProjectRepo } from '@/hooks/useProjectRepo';
import { useSessionManager } from '@/hooks/useSessionManager';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Folder,
  Download,
  Loader2,
  Trash2,
  Save,
  Sparkles,
  Layout,
} from 'lucide-react';
import { useProjectType } from '@/hooks/useProjectType';
import JSZip from 'jszip';
import { Separator } from './ui/separator';

interface ProjectDetailsDialogProps {
  project: Project;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProjectDeleted?: () => void;
  onProjectUpdated?: (project: Project) => void;
}

export function ProjectDetailsDialog({ project, open, onOpenChange, onProjectDeleted, onProjectUpdated }: ProjectDetailsDialogProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newProjectName, setNewProjectName] = useState(project.id);
  const [description, setDescription] = useState('');
  const [originalDescription, setOriginalDescription] = useState('');
  const { toast } = useToast();
  const { fs } = useFS();
  const projectsManager = useProjectsManager();
  const sessionManager = useSessionManager();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: projectType } = useProjectType(project.id);

  // Fetch Nostr repository info
  const { data: repoInfo, updateRepoAsync, isUpdating, isLoading: isLoadingRepo } = useProjectRepo(project.id);

  // Reset state when project changes or dialog opens
  useEffect(() => {
    setNewProjectName(project.id);
    setIsSaving(false);
  }, [project.id, open]);

  // Update description when repo info loads
  useEffect(() => {
    if (!isLoadingRepo) {
      const desc = repoInfo?.description || '';
      setDescription(desc);
      setOriginalDescription(desc);
    }
  }, [repoInfo?.description, isLoadingRepo]);

  const formatDistanceToNow = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
    return `${Math.floor(diffInSeconds / 31536000)} years ago`;
  };

  // Normalize the project name to show what it will become
  const normalizedProjectName = newProjectName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const hasNameChange = normalizedProjectName && normalizedProjectName !== project.id;
  const hasDescriptionChange = description !== originalDescription;
  const hasChanges = hasNameChange || hasDescriptionChange;
  const isBusy = isSaving || isUpdating;
  const showNormalizedPreview = newProjectName.trim() && newProjectName !== normalizedProjectName;

  const handleSaveProject = async () => {
    if (!hasChanges) return;

    setIsSaving(true);
    let newId = project.id;

    try {
      // Handle rename if name changed
      if (hasNameChange) {
        await projectsManager.renameProject(project.id, newProjectName);
        newId = newProjectName;
      }

      // Handle description change - update or create Nostr repo announcement
      if (hasDescriptionChange) {
        await updateRepoAsync({ description });
      }

      // Invalidate the projects query to update the sidebar
      await queryClient.invalidateQueries({ queryKey: ['projects'] });

      // Notify parent of the update
      if (onProjectUpdated) {
        const updatedProject = await projectsManager.getProject(newId);
        if (updatedProject) {
          onProjectUpdated(updatedProject);
        }
      }

      toast({
        title: "Project updated",
        description: hasNameChange
          ? `Project renamed to "${newProjectName}".`
          : "Project details saved.",
      });

      // Close the dialog first
      onOpenChange(false);

      // Navigate to the new URL if renamed
      if (hasNameChange) {
        navigate(`/project/${newProjectName}`);
      }
    } catch (error) {
      toast({
        title: "Failed to update project",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
      setNewProjectName(project.id); // Reset to original name
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteProject = async () => {
    setIsDeleting(true);
    try {
      await projectsManager.deleteProject(project.id);

      // Delete the session for this project
      await sessionManager.deleteSession(project.id);

      // Invalidate the projects query to update the sidebar
      await queryClient.invalidateQueries({ queryKey: ['projects'] });

      toast({
        title: "Project deleted",
        description: `"${project.name}" has been permanently deleted.`,
      });

      // Close the dialog first
      onOpenChange(false);

      // Navigate back to home and notify parent
      navigate('/');
      if (onProjectDeleted) {
        onProjectDeleted();
      }
    } catch (error) {
      toast({
        title: "Failed to delete project",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExportProject = async () => {
    setIsExporting(true);
    try {
      const zip = new JSZip();
      const projectPath = `/projects/${project.id}`;

      // Recursive function to add files and directories to zip from a specific project
      const addFolderToZip = async (dirPath: string, zipFolder: JSZip) => {
        try {
          const entries = await fs.readdir(dirPath, { withFileTypes: true });

          for (const entry of entries) {
            const fullPath = `${dirPath}/${entry.name}`;

            if (entry.isDirectory()) {
              // Create folder in zip and recursively add its contents
              const folder = zipFolder.folder(entry.name);
              if (folder) {
                await addFolderToZip(fullPath, folder);
              }
            } else if (entry.isFile()) {
              // Add file to zip
              try {
                const fileContent = await fs.readFile(fullPath);
                zipFolder.file(entry.name, fileContent);
              } catch (error) {
                console.warn(`Failed to read file ${fullPath}:`, error);
              }
            }
          }
        } catch (error) {
          console.warn(`Failed to read directory ${dirPath}:`, error);
        }
      };

      // Start from the project directory
      await addFolderToZip(projectPath, zip);

      // Generate zip file
      const content = await zip.generateAsync({ type: 'blob' });

      // Create download link
      const url = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${project.id}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Project exported successfully",
        description: `"${project.name}" has been downloaded as a zip file.`,
      });
    } catch (error) {
      console.error('Failed to export project:', error);
      toast({
        title: "Failed to export project",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };



  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Folder className="h-5 w-5" />
            {project.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Project Type Badge */}
          {projectType && projectType !== 'unknown' && (
            <div className="flex items-center gap-2">
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                projectType === 'nostr'
                  ? 'bg-primary/10 text-primary'
                  : 'bg-muted text-muted-foreground'
              }`}>
                {projectType === 'nostr' ? (
                  <>
                    <Sparkles className="h-3 w-3" />
                    Nostr-Powered
                  </>
                ) : (
                  <>
                    <Layout className="h-3 w-3" />
                    Vanilla React
                  </>
                )}
              </div>
            </div>
          )}

          {/* Last Modified */}
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Last Modified:</span>
            <span>{formatDistanceToNow(project.lastModified)}</span>
          </div>

          {/* Project Name */}
          <div className="space-y-2">
            <Label htmlFor="project-name">Project Name</Label>
            <Input
              id="project-name"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              disabled={isBusy}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setNewProjectName(project.id);
                }
              }}
              placeholder="Enter project name"
            />
            {showNormalizedPreview && (
              <p className="text-xs text-muted-foreground">
                Will be saved as: <span className="font-medium text-foreground">{normalizedProjectName}</span>
              </p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="project-description">Description</Label>
            <Textarea
              id="project-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              disabled={isBusy}
              placeholder="Add a description for your project..."
              className="min-h-[80px] resize-y"
            />
          </div>

          {/* Save Button */}
          {hasChanges && (
            <Button
              onClick={handleSaveProject}
              disabled={isBusy}
              className="w-full gap-2"
            >
              {isBusy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {isBusy ? 'Saving...' : 'Save Changes'}
            </Button>
          )}

          <Separator />

          {/* Export Project Button */}
          <Button
            onClick={handleExportProject}
            disabled={isExporting || isDeleting || isBusy}
            className="w-full gap-2"
            variant="outline"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {isExporting ? 'Exporting...' : 'Export Project'}
          </Button>

          {/* Delete Project Button */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                className="w-full gap-2"
                disabled={isExporting || isDeleting || isBusy}
              >
                <Trash2 className="h-4 w-4" />
                Delete Project
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Project</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{project.name}"? This action cannot be undone and will permanently delete all project data including files.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteProject}
                  disabled={isDeleting}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Project'
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </DialogContent>
    </Dialog>
  );
}
